import CheckIcon from "@material-ui/icons/Check";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import SplitDomainList from "../SplitDomainList";
import classes from "../../PublicationListSelectionSlide.module.scss";
import { BrandgenPublicationList } from "../../../../../types/campaignTypes";
import { truncateText } from "../../../../../helpers/truncateText";

const SPLIT_DASHBOARD_CONFIG = {
  public: {
    title: "BrandGen Publication Lists",
    description:
      "These are carefully curated lists of publications that we believe have a high likelihood of accepting ad traffic. If you do not wish to use any of these lists, you can create your own custom list instead.",
    nameMinWidth: 400,
    truncateLength: 100,
    containerClass: classes.domainSplitListsDashboardContainer,
  },
  custom: {
    title: "Custom Publication Lists",
    emptyMessage: "These are no custom domain lists yet!",
    withListsMessage: "These are all of the lists that you've created previously",
    nameMinWidth: 350,
    truncateLength: 80,
    containerClass: classes.domainListsDashboardContainer,
  },
} as const;

export type SplitDomainListsDashboardVariant = "public" | "custom";

type SplitDomainListsDashboardProps = {
  variant: SplitDomainListsDashboardVariant;
  domainLists: BrandgenPublicationList[];
  selectedDomainListsIds: string[];
  selectedDomainIds: string[];
  toggleSelectDomainLists: (id: string) => void;
  toggleSelectDomain: (domainId: string) => void;
};

const SplitDomainListsDashboard: FC<SplitDomainListsDashboardProps> = ({
  variant,
  domainLists,
  selectedDomainListsIds,
  selectedDomainIds,
  toggleSelectDomain,
  toggleSelectDomainLists,
}) => {
  const config = SPLIT_DASHBOARD_CONFIG[variant];

  const columns = [
    { key: "name" as const, label: "Name", minWidth: config.nameMinWidth },
    { key: "actions" as const, label: "Actions", minWidth: 100 },
  ] as const;

  const rows = domainLists
    .sort((a, b) => Date.parse(b.createdAt ?? "") - Date.parse(a.createdAt ?? ""))
    .map(({ id, name, listDomains }) => {
      const isSelected = selectedDomainListsIds.includes(id);
      const activeDomains = listDomains?.filter((d) => d.active === true) ?? [];

      return {
        id,
        name: (() => (
          <Tooltip title={name}>
            <Typography>{truncateText(name, config.truncateLength)}</Typography>
          </Tooltip>
        ))(),
        actions: (() => (
          <div className={classes.actions}>
            <Button
              className={classes.selectButton}
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => toggleSelectDomainLists(id)}
            >
              {isSelected ? "Selected" : "Select"}
              <CheckIcon />
            </Button>
          </div>
        ))(),
        collapsableConfigs: {
          collapsable: true,
          collapsableChild: (
            <SplitDomainList
              isDisabled={isSelected}
              domainsInList={activeDomains}
              toggleSelectDomain={toggleSelectDomain}
              selectedDomainIds={selectedDomainIds}
            />
          ),
        },
      };
    });

  const description =
    variant === "public"
      ? SPLIT_DASHBOARD_CONFIG.public.description
      : domainLists.length === 0
      ? SPLIT_DASHBOARD_CONFIG.custom.emptyMessage
      : SPLIT_DASHBOARD_CONFIG.custom.withListsMessage;

  return (
    <div className={config.containerClass}>
      <div className={classes.header}>
        <Typography variant="h2">{config.title}</Typography>
        <Typography>{description}</Typography>
      </div>

      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default SplitDomainListsDashboard;
